import { Component, AfterViewInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser, NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { B2BPlantNavService } from '../../plants/services/B2B-plant-nav.service';

interface Location {
    name: string;
    lat: number;
    lng: number;
    description: string;
    status: 'online' | 'abnormal' | 'offline';
}

@Component({
    selector: 'app-plant-map',
    imports: [CommonModule, NgIf, MatCardModule, MatButtonModule, MatMenuModule],
    templateUrl: './plant-map.html',
    styleUrl: './plant-map.scss'
})
export class PlantMap implements AfterViewInit, OnDestroy {

    private map: any;
    private L: any;
    public isBrowser: boolean;
    private ctrlPressed = false;
    private keydownHandler!: (e: KeyboardEvent) => void;
    private keyupHandler!: (e: KeyboardEvent) => void;
    private wheelHandler!: (e: WheelEvent) => void;

    private allBounds: any[] = [];

    private locations: Location[] = [
        { name: 'TPJ4C, Aarhus', lat: 56.1629, lng: 10.2039, description: 'S-N: TPJ4CCU013', status: 'online' },
        { name: 'GZD19, Thisted', lat: 56.9578, lng: 8.7092, description: 'S-N: GZD191204F', status: 'online' },
        { name: 'HAD18, Roskilde', lat: 55.6426, lng: 12.0901, description: 'S-N: HAD1850037', status: 'abnormal' },
        { name: 'LQD09, Odense', lat: 55.4052, lng: 10.4049, description: 'S-N: LQD092103A', status: 'online' },
        { name: 'MDD09, Esbjerg', lat: 55.4796, lng: 8.4528, description: 'S-N: MDD0921101', status: 'offline' }
    ];

    constructor(@Inject(PLATFORM_ID) private platformId: object, private b2bPlantNavService: B2BPlantNavService) {
        this.isBrowser = isPlatformBrowser(this.platformId);
    }

    async ngAfterViewInit() {
        if (!this.isBrowser) return;

        const leafletModule = await import('leaflet');
        const L = (leafletModule as any).default ?? leafletModule;
        this.L = L;

        if (L.Icon && L.Icon.Default) {
            delete (L.Icon.Default.prototype as any)._getIconUrl;

            L.Icon.Default.mergeOptions ({
                iconRetinaUrl: 'images/icons/marker-icon-2x.png',
                iconUrl: 'images/icons/marker-icon.png',
                shadowUrl: 'images/icons/marker-shadow.png'
            });
        }

        this.initMap(L);
        this.addMarkers(L);
        this.setupCtrlZoomHandler();
        this.addResetControl();
    }

    private initMap(L: any): void {
        this.map = L.map('map', {
            center: [55.58, 11.46],
            zoom: 6,
            zoomControl: true,
            scrollWheelZoom: false,
            dragging: true
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        }).addTo(this.map);
    }

    private getIconByStatus(status: 'online' | 'abnormal' | 'offline') {
        const iconPath =
            status === 'online' ? 'images/icons/marker-icon-green.png' : 
            status === 'abnormal' ? 'images/icons/marker-icon-red.png' : 
            'images/icons/marker-icon-blue.png';

        return this.L.icon({
            iconUrl: iconPath,
            shadowUrl: 'images/icons/marker-shadow.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [0, -30],
            shadowSize: [41, 41]
        });
    }

    private addMarkers(L: any): void {
        this.allBounds = [];

        this.locations.forEach(loc => {
            const icon = this.getIconByStatus(loc.status);
            const marker = L.marker([loc.lat, loc.lng], { icon }).addTo(this.map);

            this.allBounds.push([loc.lat, loc.lng]);

            const popupContent = `
                <div style="text-align:center;">
                    <b>${loc.name}</b><br>
                    <small>${loc.description}</small><br>
                    <span style="color:${
                        loc.status === 'online' ? 'green' : 
                        loc.status === 'abnormal' ? 'red' : 'blue'
                    };">${loc.status.toUpperCase()}</span><br>
                </div>
            `;

            /* With View details button - removed for now, since it doesn't work yet */
            /* const popupContent = `
                <div style="text-align:center;">
                    <b>${loc.name}</b><br>
                    <small>${loc.description}</small><br>
                    <span style="color:${
                        loc.status === 'online' ? 'green' : 
                        loc.status === 'abnormal' ? 'red' : 'blue'
                    };">${loc.status.toUpperCase()}</span><br>
                    <a href="#" id="link-${loc.description}" style="text-decoration:underline;color:blue;">
                        View Details
                    </a>
                </div>
            `; */

            marker.bindPopup(popupContent);

            marker.on('popupopen', () => {
                const linkEl = document.getElementById(`link-${loc.description}`);
                if (linkEl) {
                    L.DomEvent.on(linkEl, 'click', (e: MouseEvent) => {
                        L.DomEvent.preventDefault(e);
                        this.b2bPlantNavService.goToPlantDetails(loc.description);
                    });
                }
            });

            marker.bindTooltip(`${loc.name}`, {
                permanent: false,
                direction: 'top',
                offset: [0, -10],
            });
        });

        if (this.allBounds.length > 0) {
            setTimeout(() => {
                this.map.flyToBounds(this.allBounds, {
                    padding: [40, 40],
                    maxZoom: 10,
                    duration: 1
                });
            }, 500);
        }
    }

    public resetMapView(): void {
        if (this.allBounds.length > 0) {
            this.map.flyToBounds(this.allBounds, {
                padding: [40, 40],
                maxZoom: 10,
                duration: 0.5
            });
        }
    }

    private addResetControl(): void {
        if (!this.L || !this.map) return;

        const resetControl = this.L.Control.extend({
            options: { position: 'topright' },

            onAdd: (map:any) => {
                const container = this.L.DomUtil.create('div', 'leaflet-bar leaflet-control leaflet-control-custom');
                container.style.backgroundColor = 'white';
                container.style.width = '30px';
                container.style.height = '30px';
                container.style.cursor = 'pointer';
                container.style.display = 'flex';
                container.style.alignItems = 'center';
                container.style.justifyContent = 'center';
                container.style.fontSize = '18px';
                container.title = 'Reset map view';
                container.innerHTML = '⟳';

                container.onclick = () => this.resetMapView();

                return container;
            }
        });

        this.map.addControl(new resetControl());
    }

    private setupCtrlZoomHandler(): void {
        let hintTimeout: any;

        this.keydownHandler = (e: KeyboardEvent) => {
            if (e.key === 'Control' && !this.ctrlPressed) {
                this.ctrlPressed = true;
                this.map.scrollWheelZoom.enable();
                this.showCtrlHint(false);
            }
        };

        this.keyupHandler = (e: KeyboardEvent) => {
            if (e.key === 'Control') {
                this.ctrlPressed = false;
                this.map.scrollWheelZoom.disable();
            }
        };

        this.wheelHandler = (e: WheelEvent) => {
            if (!this.ctrlPressed) {
                e.preventDefault();

                if (hintTimeout) clearTimeout(hintTimeout);

                this.showCtrlHint(true);

                hintTimeout = setTimeout(() => {
                this.showCtrlHint(false);
                hintTimeout = null;
                }, 2000);
            }
        };

        // event listeners
        window.addEventListener('keydown', this.keydownHandler);
        window.addEventListener('keyup', this.keyupHandler);
        this.map.getContainer().addEventListener('wheel', this.wheelHandler);
    }

    private showCtrlHint(show: boolean): void {
        const mapEl = document.getElementById('map');
        if (!mapEl) return;
        if (show) {
            mapEl.classList.add('show-ctrl-hint');
        } else {
            mapEl.classList.remove('show-ctrl-hint');
        }
    }

    ngOnDestroy(): void {
        if (this.map) {
            this.map.getContainer().removeEventListener('wheel', this.wheelHandler);
            this.map.remove();
        }

        window.removeEventListener('keydown', this.keydownHandler);
        window.removeEventListener('keyup', this.keyupHandler);
    }
}